import React, { useState, useEffect, useRef } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Redirect, Route, Switch, BrowserRouter } from "react-router-dom"
import IdleTimer from 'react-idle-timer'
import { ConfigProvider } from "antd"
import { IntlProvider } from "react-intl"
import AppLocale from "lngProvider"
import { getItems } from 'util/custom'
import { setInitUrl, userSignOut } from "appRedux/actions/Auth"
import { store } from "../../NextApp"
import { handleSessionExpired } from "util/sessionExpiredHandler"
import { onLayoutTypeChange, onNavStyleChange, setThemeType } from 'appRedux/actions'
import {
  LAYOUT_TYPE_BOXED,
  LAYOUT_TYPE_FRAMED,
  LAYOUT_TYPE_FULL,
  NAV_STYLE_ABOVE_HEADER,
  NAV_STYLE_BELOW_HEADER,
  NAV_STYLE_DARK_HORIZONTAL,
  NAV_STYLE_DEFAULT_HORIZONTAL,
  NAV_STYLE_INSIDE_HEADER_HORIZONTAL,
  THEME_TYPE_DARK
} from "../../constants/ThemeSetting"
import MainApp from "./MainApp"
import SignIn from '../SignIn'
import ActivateUser from '../Activate/ActivateUser'
import OtpVerify from '../Activate/OtpVerify'
import AppAuthListener from '../AppAuthListener'
import { isEmpty } from "lodash"
import RequestResetUser from "containers/Activate/RequestResetUser"
import Providers from "@/Providers/Providers"
import { AppState } from "ReducerState"

interface AppProps {
  history: any
  match: any
  location: any
}

const RestrictedRoute = ({ component: Component, access_token, propCode, ...rest }: any) => (
  <Route
    {...rest}
    render={(routeProps: any) => {
      const hasValidToken = access_token != null && access_token !== '' && access_token.trim() !== ''
      return hasValidToken ? (
        <Component {...routeProps} />
      ) : (
        <Redirect
          to={{
            pathname: '/signin',
            search: propCode ? `?prop=${encodeURIComponent(propCode)}` : undefined,
            state: { from: routeProps.location },
          }}
        />
      )
    }}
  />
)

const applyLayoutType = (layoutType: string): void => {
  if (layoutType === LAYOUT_TYPE_FULL) {
    document.body.classList.remove('boxed-layout')
    document.body.classList.remove('framed-layout')
    document.body.classList.add('full-layout')
  } else if (layoutType === LAYOUT_TYPE_BOXED) {
    document.body.classList.remove('full-layout')
    document.body.classList.remove('framed-layout')
    document.body.classList.add('boxed-layout')
  } else if (layoutType === LAYOUT_TYPE_FRAMED) {
    document.body.classList.remove('boxed-layout')
    document.body.classList.remove('full-layout')
    document.body.classList.add('framed-layout')
  }
}

const applyNavStyle = (navStyle: string): void => {
  if (
    navStyle === NAV_STYLE_DEFAULT_HORIZONTAL ||
    navStyle === NAV_STYLE_DARK_HORIZONTAL ||
    navStyle === NAV_STYLE_INSIDE_HEADER_HORIZONTAL ||
    navStyle === NAV_STYLE_ABOVE_HEADER ||
    navStyle === NAV_STYLE_BELOW_HEADER
  ) {
    document.body.classList.add('full-scroll')
    document.body.classList.add('horizontal-layout')
  } else {
    document.body.classList.remove('full-scroll')
    document.body.classList.remove('horizontal-layout')
  }
}

const resetScrollbar = (history: any): void => {
  const clsHideScrollbar = document.getElementsByClassName('hide-scrollbar')
  const splitPathname = history.location.pathname.split('?')
  if (splitPathname[0] !== '/main/report-viewer/' && clsHideScrollbar.length > 0) {
    window.scrollTo(0, 0)
    const wrapper = document.getElementsByClassName('gx-main-content-wrapper')[0]
    if (wrapper) {
      wrapper.classList.remove('hide-scrollbar')
    }
  }
}

const App = ({ history, match, location }: AppProps) => {
  const dispatch = useDispatch()
  const { locale, navStyle, themeType, layoutType } = useSelector((state: AppState) => state.settings)
  const { authUser, initURL, checkSessionResult } = useSelector((state: AppState) => state.auth)

  const [idleTimeout, setIdleTimeout] = useState<number | null>(1000 * 60 * 15)
  const lastPropCode = useRef<string | null>(null)

  const signOut = async () => {
    const propCode = authUser?.prop_code
    dispatch(userSignOut())
    const redirectQuery = propCode ? `?prop=${encodeURIComponent(propCode)}` : ''
    history.push(`/signin${redirectQuery}`)
  }

  const onIdle = async () => {
    const { access_token } = getItems<{ access_token: string | null }>(['access_token'])
    if (access_token != null) {
      await signOut()
    }
  }

  // Equivalent to componentDidMount
  useEffect(() => {
    const { access_token } = getItems<{ access_token: string | null }>(['access_token'])

    if (!access_token && history.location.pathname.startsWith('/main')) {
      history.replace('/signin')
      return
    }

    if (access_token !== null && history.location.pathname === '/') {
      history.push('/main/dashboard')
    }

    if (access_token !== null) {
      setIdleTimeout(
        isEmpty(authUser)
          ? null
          : parseInt(String(authUser.system_config.session_timeout)) * 1000
      )
    }

    if (initURL === "") {
      dispatch(setInitUrl(history.location.pathname))
    }

    const params = new URLSearchParams(location.search)
    if (params.has("theme")) dispatch(setThemeType(params.get('theme')!))
    if (params.has("nav-style")) dispatch(onNavStyleChange(params.get('nav-style')!))
    if (params.has("layout-type")) dispatch(onLayoutTypeChange(params.get('layout-type')!))

    if (authUser?.prop_code) {
      lastPropCode.current = authUser.prop_code
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Equivalent to componentDidUpdate — authUser changed
  useEffect(() => {
    setIdleTimeout(
      isEmpty(authUser)
        ? null
        : parseInt(String(authUser?.system_config?.session_timeout)) * 1000
    )
    if (authUser?.prop_code) {
      lastPropCode.current = authUser.prop_code
    }
  }, [authUser])

  // Equivalent to componentDidUpdate — checkSessionResult changed
  useEffect(() => {
    if (checkSessionResult?.valid === false) {
      handleSessionExpired(store, checkSessionResult.message)
    }
  }, [checkSessionResult])

  useEffect(() => { applyLayoutType(layoutType ?? '') }, [layoutType])
  useEffect(() => { applyNavStyle(navStyle ?? '') }, [navStyle])
  useEffect(() => { resetScrollbar(history) }, [history.location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (themeType === THEME_TYPE_DARK) {
      document.body.classList.add('dark-theme')
    }
  }, [themeType])

  const { access_token } = getItems<{ access_token: string | null }>(['access_token'])
  const propCode = authUser?.prop_code || lastPropCode.current || ''

  if (!access_token && history.location.pathname.startsWith('/main')) {
    return <Redirect to="/signin" />
  }

  const currentAppLocale = AppLocale[locale.locale as keyof typeof AppLocale]

  return (
    <>
      <IdleTimer
        onActive={() => { /* keep timer alive */ }}
        onIdle={onIdle}
        timeout={idleTimeout ?? undefined}
        startOnMount
      />
      <ConfigProvider locale={currentAppLocale.antd}>
        <IntlProvider
          locale={currentAppLocale.locale}
          messages={currentAppLocale.messages}>
          <Providers>
            <AppAuthListener />
            {(!process.env.NODE_ENV || process.env.NODE_ENV === 'development') ? (
              <Switch>
                <Route exact path='/signin' component={SignIn} />
                <Route exact path='/activate/' component={ActivateUser} />
                <Route exact path='/OTP/verify/' component={OtpVerify} />
                <Route exact path='/request/:action' component={RequestResetUser} />
                <RestrictedRoute
                  path='/main'
                  access_token={access_token}
                  propCode={propCode}
                  component={MainApp}
                />
                <Route exact path={`${match.url}`} component={SignIn} />
              </Switch>
            ) : (
              <Switch>
                <BrowserRouter basename={`${process.env.REACT_APP_PUBLIC_URL}`}>
                  <Route exact path='/signin' component={SignIn} />
                  <Route exact path='/activate/' component={ActivateUser} />
                  <Route exact path='/OTP/verify/' component={OtpVerify} />
                  <Route exact path='/request/:action' component={RequestResetUser} />
                  <RestrictedRoute
                    path='/main'
                    access_token={access_token}
                    propCode={propCode}
                    component={MainApp}
                  />
                  <Route exact path={`${match.url}`} component={SignIn} />
                </BrowserRouter>
              </Switch>
            )}
          </Providers>
        </IntlProvider>
      </ConfigProvider>
    </>
  )
}

export default App
